/**
 * Workflow Template access rules for Business Unit Admins (includes BU read-only).
 * Enterprise Admin paths do not use these filters — callers should skip when the user is an EA.
 */

import { isGlobalTemplateCategory } from "./workflowTemplatePlaceholderSyntax";

function normalizeCategoryAndBu(template: {
  category?: string;
  businessUnitId?: string | null;
  Category?: string;
  BusinessUnitId?: string | null;
}): { category: string; businessUnitId: string } {
  const category = String(template.category ?? template.Category ?? "")
    .trim()
    .toLowerCase();
  const businessUnitId = String(template.businessUnitId ?? template.BusinessUnitId ?? "")
    .trim()
    .toLowerCase();
  return { category, businessUnitId };
}

/** BUA may edit / delete / change status only for own BU-scoped templates (not global or other BU). */
export function businessUnitAdminCanMutateTemplate(
  template: {
    category?: string;
    businessUnitId?: string | null;
    Category?: string;
    BusinessUnitId?: string | null;
  },
  loggedUserBu: string
): boolean {
  const bu = (loggedUserBu || "").trim().toLowerCase();
  const { category, businessUnitId: tbu } = normalizeCategoryAndBu(template);
  return category === "businessunit" && !!tbu && tbu === bu;
}

/**
 * BUA may generate from global templates or from BU-scoped templates for their own BU only.
 * Legacy rows missing valid category/BU are not allowed.
 */
export function businessUnitAdminCanGenerateFromTemplate(
  template: {
    category?: string;
    businessUnitId?: string | null;
    Category?: string;
    BusinessUnitId?: string | null;
  },
  loggedUserBu: string
): boolean {
  const bu = (loggedUserBu || "").trim().toLowerCase();
  const rawCategory = String(template.category ?? template.Category ?? "");
  if (isGlobalTemplateCategory(rawCategory)) return true;
  const { category, businessUnitId: tbu } = normalizeCategoryAndBu(template);
  if (category === "businessunit" && tbu && tbu === bu) return true;
  return false;
}

/** List rows visible to BUA / BUARO: global or own BU only. */
export function filterWorkflowTemplatesForBusinessUnitAdmin<
  T extends { category?: string; businessUnitId?: string | null; Category?: string; BusinessUnitId?: string | null },
>(templates: T[], loggedUserBu: string): T[] {
  const bu = (loggedUserBu || "").trim().toLowerCase();
  return templates.filter((t) => {
    const rawCategory = String(t.category ?? t.Category ?? "");
    if (isGlobalTemplateCategory(rawCategory)) return true;
    const { category, businessUnitId: templateBu } = normalizeCategoryAndBu(t);
    if (category === "businessunit" && templateBu && templateBu === bu) return true;
    return false;
  });
}
